import { useTranslation } from 'react-i18next';
import { Box, HStack } from 'native-base';

import { CurrencyText, Text } from '@pimm/base';
import { useSiteTime } from '@app/features/store-core';
import { SalesBlock, SalesDaypart } from '../types';
import { LAYOUT } from '../consts';

export type SalesRow = {
  key: string;
  label: string;
  getValue: (slot: SalesBlock, now: Date) => number | null;
  showInManager?: boolean;
  isBold?: boolean;
  isVariance?: boolean;
  isManagerCombined?: boolean; // "Actual / Forecast" combined row
  isForecastData?: boolean; // hidden when displayForecastedSales is false
};

type SalesTableRowProps = {
  row: SalesRow;
  tableData?: Partial<SalesDaypart>;
  displayForecastedSales?: boolean;
};

export const SalesTableRow = ({ row, tableData, displayForecastedSales }: SalesTableRowProps) => {
  const { t } = useTranslation();
  const siteTime = useSiteTime();
  const now = siteTime.today();

  return (
    <HStack alignItems="stretch">
      {/* Row label */}
      <Box w={LAYOUT.stickyLabelW} px={2.5} py={1} minH="22px" justifyContent="center" borderRightWidth={1} borderColor="gray.200">
        <Text size={{ base: 'xs', xl: 'sm' }} fontWeight={row.isBold ? 600 : 400} color="gray.900" flexShrink={1} lineHeight="xs">
          {t('sales_tracker:' + row.label)}
        </Text>
      </Box>

      {/* Data cells */}
      {tableData?.blocks?.map(block => {
        const value = row.key === 'variance_cumulative' && !displayForecastedSales ? block.salesCumActual : row.getValue(block, now);
        const isNotCompleted = !block.hasActual;
        const showDash = isNotCompleted && row.key !== 'forecast' && (row.key !== 'actual_forecast' || !displayForecastedSales);
        const isPositive = !isNotCompleted && row.isVariance && value != null && value > 0;
        const isNegative = !isNotCompleted && row.isVariance && value != null && value < 0;

        const cellBg = isNotCompleted ? 'grayCool.50' : isPositive ? 'blueLight.50' : isNegative ? 'error.50' : undefined;
        const textColor = isNegative ? 'error.600' : isPositive ? 'blueLight.700' : isNotCompleted ? 'gray.500' : 'gray.800';

        return (
          <HStack
            key={`sales-table.${row.key}.${block.startTime.toISOString()}`}
            flex={1}
            alignItems="center"
            justifyContent="center"
            p={0.5}
            borderLeftWidth={1}
            borderColor="gray.200"
            bg={cellBg}
          >
            {showDash ? (
              <Text size={{ base: 'xs', xl: 'sm' }} color="gray.500">
                -
              </Text>
            ) : (
              <CurrencyText
                size={{ base: 'xs', xl: 'sm' }}
                fontWeight={row.isBold ? 600 : 400}
                color={textColor}
                hideFlatZero
                value={Math.round(value ?? 0)}
              />
            )}
          </HStack>
        );
      })}
    </HStack>
  );
};
